import Intercom, { shutdown as shutdownIntercom } from '@intercom/messenger-js-sdk'
import { useAuth } from 'hooks/auth'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'

const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID

const DISABLED_ROUTES = ['/dispatcher/ai-hub', '/dispatcher/load-hub', '/dispatcher/dispatch-help', '/dispatcher/box-truck-', '/fuel-ai']

interface UseIntercomOptions {
    enabledRoutes?: string[]
}

/**
 * Loads the Intercom messenger, with its own launcher, on the routes it is enabled for, and shuts it down elsewhere.
 * The general agent has its own page (`/agent`) and no floating launcher, so nothing here hides the messenger for it.
 * @param options.enabledRoutes - Array of route prefixes where Intercom should be shown (default: ['/dispatcher'])
 */
export function useIntercom(options: UseIntercomOptions = {}) {
    const { enabledRoutes = ['/dispatcher'] } = options
    const { user } = useAuth()
    const location = useLocation()

    const isOnEnabledRoute = enabledRoutes.some((route) => location.pathname.startsWith(route))
    const isOnDisabledRoute = DISABLED_ROUTES.some((route) => location.pathname.startsWith(route))

    useEffect(() => {
        if (!isOnEnabledRoute || isOnDisabledRoute) {
            shutdownIntercom()
            return
        }

        if (!INTERCOM_APP_ID) {
            return
        }

        Intercom({
            app_id: INTERCOM_APP_ID,
            name: user?.name,
            email: user?.email,
            user_id: user?.id,
            horizontal_padding: 60,
            vertical_padding: 80,
        })

        return () => {
            shutdownIntercom()
        }
    }, [isOnEnabledRoute, isOnDisabledRoute, user?.name, user?.email, user?.id])
}
